import React, { useId } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';
import { BUTTON_QUIET, FIELD, SUBSECTION_TITLE } from './adminStyles';
import type { ShootDayContact } from '../../utils/shootDaySource';
import { matchKnownContact, type KnownContact } from '../../utils/knownContacts';

/**
 * One compact add-a-row list, reused for client contacts, featured guests and
 * extra/freelance crew — people with no Portal OS account, typed in by hand.
 *
 * Lifted out of CallSheetBuilder.tsx on 13 Sep 2026 when call sheets became
 * standalone; the builder page had grown past the point where one file was
 * doing anyone a favour.
 *
 * **Suggestions, 24 Sep 2026.** The name field offers everyone typed into
 * this kind of list before, and picking one fills in the role, phone and
 * email that came with them last time. It only ever fills a field that is
 * still EMPTY — a number corrected on this sheet is never overwritten by an
 * older one, which is the difference between a shortcut and a trap.
 *
 * A plain `<datalist>` rather than a custom menu, deliberately: it is the
 * browser's own, so it works with the keyboard, on a phone, and with a
 * partial name typed in any order, and it adds nothing to maintain.
 */
interface CallSheetPeopleProps {
  title: string;
  hint: string;
  items: ShootDayContact[];
  addLabel: string;
  showRemote?: boolean;
  /** People typed into this list before. Empty is fine — the field just behaves as it did. */
  known?: KnownContact[];
  onAdd: () => void;
  onUpdate: (index: number, patch: Partial<ShootDayContact>) => void;
  onRemove: (index: number) => void;
}
export function CallSheetPeople({
  title,
  hint,
  items,
  addLabel,
  showRemote,
  known = [],
  onAdd,
  onUpdate,
  onRemove
}: CallSheetPeopleProps) {
  /* Unique per instance, so the three lists on a call sheet do not share one
     suggestion set through a duplicated id. */
  const listId = `known-${useId()}`;
  function nameChanged(index: number, item: ShootDayContact, typed: string) {
    const person = matchKnownContact(known, typed);
    if (!person) {
      onUpdate(index, {
        name: typed
      });
      return;
    }
    onUpdate(index, {
      name: person.name,
      /* Only what is still blank. Whatever is on this sheet was typed for
         this day and wins. */
      role: item.role.trim() ? item.role : person.role,
      phone: item.phone.trim() ? item.phone : person.phone,
      email: item.email.trim() ? item.email : person.email
    });
  }
  return <div className="mt-8" data-id="element-700" data-mp="ppf6zgkhlqk0">
      <span className={SUBSECTION_TITLE} data-id="element-701" data-mp="ppp21ike2nwk">{title}</span>
      <p className="label-sm mt-2 max-w-xl leading-relaxed text-paper/35" data-id="element-702" data-mp="pn2v9l5bocfe">{hint}</p>

      {known.length > 0 && <datalist id={listId} data-id="element-703" data-mp="fr9bpht6rio4">
          {known.map(person => <option key={person.name} value={person.name} label={[person.role, person.phone].filter(Boolean).join('  ·  ') || undefined} data-id="element-704" data-mp="fpeq01a3bmiz" />)}
        </datalist>}

      {items.length > 0 && <ul className="mt-4 space-y-4" data-id="element-705" data-mp="1iisk8monuga9">
          {items.map((item, index) => <li key={index} className="flex items-start gap-4 border border-paper/20 p-4" data-id="element-706" data-mp="1dla8koj9dmyk" data-mpi={index}>
              <div className="grid flex-1 grid-cols-2 gap-4 sm:grid-cols-4" data-id="element-707" data-mp="1rk9zy3y5ofyf">
                <input value={item.name} onChange={event => nameChanged(index, item, event.target.value)} placeholder="Name" list={known.length > 0 ? listId : undefined} autoComplete="off" className={FIELD} data-id="element-708" data-mp="1ri75n7pz00nw" />
                <input value={item.role} onChange={event => onUpdate(index, {
            role: event.target.value
          })} placeholder="Role / org (optional)" className={FIELD} data-id="element-709" data-mp="18lxkk0gni9ue" />
                <input value={item.phone} onChange={event => onUpdate(index, {
            phone: event.target.value
          })} placeholder="Phone (optional)" className={FIELD} data-id="element-710" data-mp="g3a8tbnjo75z" />
                <input value={item.email} onChange={event => onUpdate(index, {
            email: event.target.value
          })} placeholder="Email (optional)" className={FIELD} data-id="element-711" data-mp="trbuae617war" />
              </div>
              {showRemote && <label className="flex shrink-0 items-center gap-2 self-center label-sm text-paper/40" data-id="element-712" data-mp="18fybklf7yy4m">
                  <input type="checkbox" checked={Boolean(item.remote)} onChange={event => onUpdate(index, {
            remote: event.target.checked
          })} className="h-3.5 w-3.5" data-id="element-713" data-mp="1chjschuvjwk3" />
                  Remote
                </label>}
              <button type="button" onClick={() => onRemove(index)} aria-label={`Remove ${title.toLowerCase()} row`} className="shrink-0 self-center text-paper/30 transition-colors duration-150 ease-out hover:text-paper" data-id="element-714" data-mp="3jb5iti7dy488">
                <XIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-715" data-mp="3bvtitivnnipv" />
              </button>
            </li>)}
        </ul>}

      <button type="button" onClick={onAdd} className={`${BUTTON_QUIET} mt-4 flex items-center gap-2`} data-id="element-716" data-mp="31zywods835ic">
        <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-717" data-mp="3imly21hod7e" />
        {addLabel}
      </button>
    </div>;
}